import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ShellIdentity } from '../../core/shell-identity';
import type { FleetManager } from '../managers/fleet-manager';
import type { FleetGateway } from '../../core/bridge/connect-client';
import { getSession, Perm } from '@vitesse/base-ui';
import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-icon';

/*
 * <vitesse-claim-dialog> — the quick "claim a nearby gateway" popup. The ONE claim
 * surface in the agent app (the full fleet/admin dashboard is a separate UI, TBD).
 *
 * Lists the enrolled-but-unclaimed controllers DiscoverNearby found on the user's
 * network and claims one with a single click — NO code. Server-side the claim is
 * proven by LAN-presence (same network the box enrolled from); the optional
 * 4-digit access-point code is a FUTURE stronger/cross-network step (the manager's
 * claim() already accepts it). The Claim button is admin-gated by authz.can — and
 * the server re-enforces it regardless of the UI (members get PermissionDenied).
 *
 * Wraps <vitesse-dialog> (native top-layer modal). Controlled `open`; emits
 * `dialog-close`. Subscribes to the FleetManager so the list stays live while open.
 */
@customElement('vitesse-claim-dialog')
export class VitesseClaimDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ attribute: false }) fleetManager?: FleetManager;
  @property({ attribute: false }) identity?: ShellIdentity;

  @state() private nearby: FleetGateway[] = [];
  @state() private claimingFp = '';
  @state() private error = '';
  private names = new Map<string, string>(); // per-box name override (fingerprint → label)

  constructor() {
    super();
    new LocaleController(this);
  }

  private readonly onFleetChange = (): void => {
    this.nearby = this.fleetManager?.nearby ?? [];
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.fleetManager?.addEventListener('change', this.onFleetChange);
    this.onFleetChange();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.fleetManager?.removeEventListener('change', this.onFleetChange);
  }

  private get canClaim(): boolean {
    /*
     * Through the SESSION, not `can` directly.
     *
     * Plain `can` answers from the role catalog alone — it never consults the
     * authored grant matrix and knows nothing about which surface it is on. The
     * server decides this with AllowFrom, which consults both. So an administrator
     * who revoked gateway:claim for this person, or granted it only on the fleet
     * surface, saw the button here stay enabled and the click fail. The session
     * helper's `can` is `canFrom(identity, surface, …)` — the same composition the
     * server runs.
     */
    return getSession()?.can(Perm.GatewayClaim) ?? false;
  }

  private close(): void {
    this.error = '';
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  }

  private nameFor(g: FleetGateway): string {
    return this.names.get(g.deviceFingerprint) ?? g.displayName ?? g.hostname ?? '';
  }

  private async claim(g: FleetGateway): Promise<void> {
    if (!this.fleetManager || this.claimingFp) return;
    this.claimingFp = g.deviceFingerprint;
    this.error = '';
    try {
      // Code-free quick claim — LAN-presence is the server-side proof. The name is
      // optional; default to the box's hostname.
      await this.fleetManager.claim(g.deviceFingerprint, this.nameFor(g).trim());
      // The org now owns this box — it IS the gateway the app views. Tell the
      // shell so it resolves to it without waiting on a fleet re-poll.
      this.dispatchEvent(new CustomEvent('gateway-claimed', {
        detail: g.deviceFingerprint, bubbles: true, composed: true,
      }));
      // Box just left "nearby"; if that was the last one, close the popup.
      if ((this.fleetManager.nearby ?? []).length === 0) this.close();
    } catch (e) {
      this.error = t('fleet.claimError', { error: e instanceof Error ? e.message : String(e) });
    } finally {
      this.claimingFp = '';
    }
  }

  private row(g: FleetGateway) {
    const short = g.deviceFingerprint.slice(0, 12);
    const busy = this.claimingFp === g.deviceFingerprint;
    return html`
      <div class="row">
        <div class="row-main">
          <input
            class="name"
            .value=${this.nameFor(g)}
            placeholder=${t('fleet.fieldNamePlaceholder')}
            ?disabled=${!this.canClaim || busy}
            @input=${(e: Event) => this.names.set(g.deviceFingerprint, (e.target as HTMLInputElement).value)}
          />
          <div class="meta">
            <span class="fp" title=${g.deviceFingerprint}>${t('fleet.fingerprint')}: ${short}…</span>
            ${g.softwareVersion ? html`<span>· ${g.softwareVersion}</span>` : nothing}
          </div>
        </div>
        ${this.canClaim
          ? html`<vitesse-gated .rpc=${FleetControl.method.claim} inline
              ><vitesse-button
                class="claim-btn"
                color="blue"
                filled
                icon="shield-check"
                ?disabled=${busy}
                @click=${() => this.claim(g)}
                >${busy ? t('fleet.claiming') : t('fleet.claim')}</vitesse-button
              ></vitesse-gated
            >`
          : nothing}
      </div>
    `;
  }

  /** The claim body — shared by the modal popup and the embedded first-run card. */
  private body() {
    return html`
      ${this.canClaim ? nothing : html`<p class="note">${t('fleet.claimAdminOnly')}</p>`}
      ${this.nearby.length === 0
        ? html`<p class="empty"><span class="scan-dot"></span>${t('fleet.nearbyEmpty')}</p>`
        : html`<div class="list">${this.nearby.map((g) => this.row(g))}</div>`}
      ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
    `;
  }

  render() {
    return html`
      <vitesse-dialog
        .open=${this.open}
        heading=${t('fleet.claimTitle')}
        closeLabel=${t('fleet.cancel')}
        @dialog-close=${() => this.close()}
      >
        ${this.body()}
      </vitesse-dialog>
    `;
  }

  static styles = css`
    /* First-run (embedded): a centered welcome card over the app background. */
    .first-run {
      min-height: 100vh;
      display: grid;
      place-items: center;
      padding: 24px;
      box-sizing: border-box;
    }
    .card {
      width: min(520px, 100%);
      background: var(--surface, #15171b);
      border: 1px solid var(--border-color);
      border-radius: 14px;
      padding: 32px 28px;
      text-align: center;
      box-shadow: 0 12px 48px color-mix(in srgb, var(--shadow-color) 35%, transparent);
    }
    .card h1 {
      margin: 12px 0 6px;
      font-size: 20px;
      font-weight: 650;
      color: var(--text-primary);
    }
    .card .list,
    .card .note,
    .card .empty {
      text-align: left;
      margin-top: 16px;
    }
    .scan-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      margin-right: 8px;
      border-radius: 50%;
      background: var(--blue, #1a8cd8);
      animation: scan-pulse 1.2s ease-in-out infinite;
      vertical-align: middle;
    }
    @keyframes scan-pulse {
      0%, 100% { opacity: 0.25; transform: scale(0.8); }
      50% { opacity: 1; transform: scale(1.15); }
    }
    .note,
    .empty {
      margin: 0 0 12px;
      font-size: 13px;
      color: var(--text-secondary);
    }
    .list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border: 1px solid var(--border-color);
      border-radius: 10px;
      background: var(--bg-secondary);
    }
    .row-main {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .name {
      width: 100%;
      padding: 7px 10px;
      font-size: 14px;
      color: var(--text-primary);
      background: var(--bg-primary);
      border: 1px solid var(--border-color);
      border-radius: 7px;
      outline: none;
    }
    .name:focus {
      border-color: var(--blue, #1a8cd8);
    }
    .name:disabled {
      opacity: 0.6;
    }
    .meta {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      font-size: 12px;
      color: var(--text-tertiary, var(--text-secondary));
    }
    .fp {
      font-family: monospace;
    }
    /* Only the layout the dialog imposes — the button's own look is
       <vitesse-button filled>'s. */
    .claim-btn {
      flex-shrink: 0;
    }
    .error {
      margin: 12px 0 0;
      font-size: 13px;
      color: var(--red, #e0245e);
    }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-claim-dialog': VitesseClaimDialog;
  }
}
